import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { GiftIcon as Gift, TagIcon as Tag } from "@phosphor-icons/react";
import { isNativeApp } from "@/lib/nativeBridge";

/**
 * Tiny inline banner shown on every paid surface for users who have
 * an active Comp List entry on file. Lets them know the discount is
 * auto-applied — they don't need to type a promo code.
 *
 * Self-fetches `/api/comp/me`. Renders nothing if the user has no
 * active comp, so it's safe to drop on every checkout panel.
 *
 * Designed to live ABOVE the PromoCodeInput on every checkout
 * surface. Buyers can still type a code (we always pick the better
 * of the two discounts).
 */
export default function CompBanner({ className = "" }) {
  const [state, setState] = useState({ loading: true, comp: null });
  const native = isNativeApp();

  useEffect(() => {
    if (native) return;
    let cancelled = false;
    api.get("/comp/me")
      .then((r) => {
        if (cancelled) return;
        if (r.data?.has_comp) setState({ loading: false, comp: r.data });
        else setState({ loading: false, comp: null });
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, comp: null });
      });
    return () => { cancelled = true; };
  }, []);

  if (native || state.loading || !state.comp) return null;

  const isFree = state.comp.kind === "free";
  return (
    <div
      className={`flex items-start gap-3 rounded-lg border border-[#C9A227]/40 bg-gradient-to-r from-[#FBF7EC] to-[#F4E5B0]/40 px-3 py-2.5 text-sm ${className}`}
      data-testid="comp-banner"
    >
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-white ring-1 ring-[#C9A227]/30 flex items-center justify-center">
        {isFree ? (
          <Gift size={14} weight="duotone" className="text-[#8A6A00]" />
        ) : (
          <Tag size={14} weight="duotone" className="text-[#8A6A00]" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8A6A00] leading-tight">
          Comp on file
        </p>
        <p className="text-[13px] font-semibold text-ink leading-snug mt-0.5">
          {state.comp.label}
        </p>
        <p className="text-[11.5px] text-[#6B6B6B] mt-0.5 leading-snug">
          Auto-applied at checkout — no promo code needed. You can still type one below to compare.
        </p>
      </div>
    </div>
  );
}
